<div (keydown.enter)="submit()" class="flex flex-col gap-5">

  <!-- Main product block -->
  <div class="flex flex-col gap-3">

    <!-- Row 1: product (flexible) + quantity + unit price, all on one line -->
    <div class="flex items-end gap-3">
      <div class="flex-1 min-w-0">
        <elcia-product-dropdown [control]="form.controls.productId" [isMulti]="false"
                                [productType]="mainProductTypes"
                                data-testid="customer-product-product-dropdown"/>
      </div>
      <div class="w-24 shrink-0">
        <elcia-numeric-input [control]="form.controls.quantity" [label]="'product-customer.quantity' | translate"
                             [min]="1"
                             data-testid="customer-product-quantity-input"/>
      </div>
      <div *canAccessFinances class="w-32 shrink-0">
        <elcia-numeric-input [control]="form.controls.price"
                             [label]="'product-customer.unit-price' | translate"
                             [min]="0" [maxFractionDigits]="2" [suffix]="'€'"
                             data-testid="customer-product-price-input"/>
      </div>
    </div>

    <!-- Visible to everyone: without finance access the price field is hidden but still required -->
    @if (form.controls.price.invalid && form.controls.price.touched) {
      <span class="text-xs text-red-600" data-testid="customer-product-no-catalogue-price">
        {{ 'product-customer.no-catalogue-price' | translate }}
      </span>
    }

    <!-- Row 2: start date + end date — hidden for a one-shot product (no validity period) -->
    @if (!isOneShotProduct()) {
      <div class="flex items-end gap-3">
        <div class="flex-1">
          <elcia-date-picker [control]="form.controls.startDate" [label]="'app.start-date' | translate"
                             data-testid="customer-product-start-date"/>
        </div>
        <div class="flex-1">
          <elcia-date-picker [control]="form.controls.endDate" [label]="'app.end-date' | translate"
                             data-testid="customer-product-end-date"/>
        </div>
      </div>
    }

    <!-- Computed amount — subtle panel tied to the fields above -->
    @if (mainAmount(); as amount) {
      <div card2 class="flex items-center justify-between gap-2 text-sm">
        <span class="text-surface-500">{{ 'product-customer.amount' | translate }}</span>
        <span class="font-semibold tabular-nums">{{ amount | number:'1.2-2' }} €</span>
      </div>
    }
  </div>

  <!-- Description -->
  <elcia-text-area [control]="form.controls.description" [label]="'product-customer.description' | translate"
                   [rows]="3"/>

  <!-- Option flags — framed block, one settings-style row each (label left, switch right) -->
  <div class="flex flex-col gap-2.5 rounded-lg border border-primary-50 px-3.5 py-3">
    <div class="flex items-center justify-between gap-3">
      <elcia-label [label]="'product-customer.include-in-invoicing' | translate"/>
      <elcia-toggle-switch [control]="form.controls.mustIncludeInInvoicing"/>
    </div>
    @if (isEdit()) {
      <div class="flex items-center justify-between gap-3">
        <elcia-label [label]="'product-customer.active' | translate"/>
        <elcia-toggle-switch [control]="form.controls.isActive"/>
      </div>
    }
  </div>

  <!-- Extra features section — licence products only -->
  @if (isLicenceProductSelected()) {
    <div class="flex flex-col gap-3 pt-4 border-t border-primary-50">

      <!-- Section header -->
      <div class="flex items-center justify-between">
        <elcia-label [label]="'product-customer.features' | translate"></elcia-label>
        <elcia-button (onClick)="addFeature()"
                      [icon]="'plus'"
                      [label]="'product-customer.add-feature' | translate"
                      [severity]="'secondary'"/>
      </div>

      <!-- Empty state -->
      @if (features.controls.length === 0) {
        <div
          class="flex justify-center items-center py-6 text-sm text-surface-400 rounded-xl border border-dashed border-primary-50">
          {{ 'product-customer.features-empty' | translate }}
        </div>
      }

      <!-- Feature rows — each in its own card for clear alignment and breathing room -->
      @for (feature of features.controls; track $index) {
        <div card2 class="flex flex-col gap-3 ">

          <!-- Line 1: product (flexible) + unit price + remove -->
          <div class="flex items-end gap-3">
            <!-- disabledProductIds greys out features already bundled in the base licence -->
            <div class="flex-1 min-w-0">
              <elcia-product-dropdown [control]="feature.controls.productId"
                                      [disabledProductIds]="disabledFeatureProductIds()"
                                      [isMulti]="false"
                                      [productType]="featureProductType"/>
            </div>
            <div class="w-32 shrink-0" *canAccessFinances>
              <elcia-numeric-input [control]="feature.controls.price"
                                   [label]="'product-customer.unit-price' | translate"
                                   [min]="0" [maxFractionDigits]="2" [suffix]="'€'"/>
            </div>
            <elcia-button (onClick)="removeFeature($index)" [icon]="'minus'" [severity]="'secondary'"
                          [variant]="'text'"/>
          </div>

          @if (feature.controls.price.invalid && feature.controls.price.touched) {
            <span class="text-xs text-red-600">{{ 'product-customer.no-catalogue-price' | translate }}</span>
          }

          <!-- Line 2: invoicing toggle — same settings-row style as the main product (label left, switch right) -->
          <div class="flex items-center justify-between gap-3">
            <elcia-label [label]="'product-customer.include-in-invoicing' | translate"/>
            <elcia-toggle-switch [control]="feature.controls.mustIncludeInInvoicing"/>
          </div>

          <!-- Computed amount for this feature — right-aligned, only when a price applies -->
          @if (featureAmounts()[$index]; as amount) {
            <div class="flex justify-end text-xs">
              <span class="font-medium text-surface-500 tabular-nums">{{ amount | number:'1.2-2' }} €</span>
            </div>
          }
        </div>
      }
    </div>
  }

</div>
